import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import CheckoutSummary from './CheckoutSummary';
import CheckoutPageShell from './CheckoutPageShell';
import { useCheckoutPageSession } from '../hooks/useCheckoutPageSession';
import { finalizeSubmittedBooking, releasePendingBooking, isBookingSubmitted } from '../utils/checkoutFlowUtil';
import { CHECKOUT_EVENTS } from '../constants/bookingConst';
import { buildGuestPartialPaymentFields, saveGuestPartialPaymentChoice } from '../utils/partialPaymentUtil';

/**
 * The court-booking checkout step in its own browser tab
 * (/checkout/:sessionId).
 *
 * Opened by the booking tab (see hooks/useCheckoutTab.js). Shows
 * CheckoutSummary for the booking it was handed, performs the submit /
 * release writes itself, reports each outcome back to the booking tab, and
 * closes itself when the checkout is over — the booking tab then shows the
 * confirmation (or returns the guest to the details form).
 *
 * @returns {JSX.Element}
 */
function CheckoutPage() {
  const { sessionId } = useParams();
  const { config } = useConfig();
  const { session, phase, updateRecord, post, endCheckout, runOnce } = useCheckoutPageSession(sessionId);

  const bookingData = session?.bookingData || null;
  const bookingKeys = session?.bookingKeys || null;

  /**
   * Receipt attached — this is what submits the booking. The stored session
   * is updated first, so a reload shows the booking as submitted even if the
   * writes below are still running.
   *
   * @param {boolean} partialPayment - the guest's final "Partial payment"
   *   tick on this screen, saved onto the booking for the admin.
   */
  const handleReceiptUploaded = async (partialPayment) => {
    if (!bookingData) return;
    const guestPartialFields = buildGuestPartialPaymentFields(partialPayment, config);
    // uploadPaymentScreenshot has already stored the image in
    // bookingReceipts/ and set the flag on the records; mirror the flag here
    // so hasReceipt() sees it without a re-read.
    const finalBookingData = { ...bookingData, ...guestPartialFields, status: 'pending', hasPaymentScreenshot: true };
    updateRecord(finalBookingData);
    post(CHECKOUT_EVENTS.RECEIPT_UPLOADED, { bookingData: finalBookingData });
    // Not allowed to block the submission: the receipt is already in, and
    // the choice from Booking details is on the records as a fallback.
    await saveGuestPartialPaymentChoice(db, bookingKeys, guestPartialFields).catch((error) => {
      console.error('Saving the partial payment choice failed (booking still submitted):', error);
    });
    await finalizeSubmittedBooking({ db, bookingData: finalBookingData, bookingKeys, config });
  };

  /**
   * "Submit Booking" — the booking is already submitted by this point; hand
   * the guest back to the booking tab for the confirmation.
   */
  const handleSubmitBooking = () => {
    endCheckout(CHECKOUT_EVENTS.SUBMITTED, { bookingData });
  };

  /**
   * Releases the reservation (cancel or timeout), unless it turns out to be
   * submitted already — then it is treated as a normal submit.
   * @param {string} cancelReason
   * @param {string} eventType - CHECKOUT_EVENTS value to report on release.
   */
  const releaseAndEnd = (cancelReason, eventType) => runOnce(async () => {
    const { alreadySubmitted } = await releasePendingBooking({ db, bookingData, bookingKeys, cancelReason });
    if (alreadySubmitted) {
      endCheckout(CHECKOUT_EVENTS.SUBMITTED, { bookingData: { ...bookingData, status: 'pending' } });
      return;
    }
    endCheckout(eventType);
  });

  /**
   * The × on the card. Before the receipt it cancels the reservation; after
   * it, the booking is live and × just finishes like Submit Booking.
   */
  const handleBack = () => {
    if (isBookingSubmitted(bookingData)) {
      handleSubmitBooking();
      return;
    }
    releaseAndEnd('guest_cancelled', CHECKOUT_EVENTS.CANCELLED);
  };

  const handleExpire = () => {
    releaseAndEnd('payment_timeout', CHECKOUT_EVENTS.EXPIRED);
  };

  return (
    <CheckoutPageShell
      phase={phase}
      inactiveMessage="We couldn’t find a booking in progress for this page. Please go back to the booking page and start again."
    >
      {bookingData && (
        <CheckoutSummary
          bookingData={bookingData}
          bookingKeys={bookingKeys}
          onPaymentDone={handleSubmitBooking}
          onReceiptUploaded={handleReceiptUploaded}
          onExpire={handleExpire}
          onBack={handleBack}
        />
      )}
    </CheckoutPageShell>
  );
}

export default CheckoutPage;
